import { ColumnDef } from '@tanstack/react-table';
import {
  Calendar,
  Building2,
  Mail,
  Phone,
} from 'lucide-react';
import { ExtendedBrand, formatDate, getLenderBadgeColor } from './BrandTypes';

// Table columns
export const getBrandColumns = (page: number, pageSize: number): ColumnDef<ExtendedBrand, any>[] => [
  {
    id: 'serialNo',
    header: 'Sr. No',
    cell: ({ row }) => {
      // Use the serialNumber property from the Brand object if available,
      // otherwise fall back to calculating it (for backward compatibility)
      const serialNo = row.original.serialNumber || ((page - 1) * pageSize + row.index + 1);
      return (
        <span className="text-xs font-medium">
          {serialNo}
        </span>
      );
    },
    size: 50 // Make column narrower
  },
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    cell: ({ row }) => (
      <div className="flex flex-col">
        {/* Row 1: Icon + Name + EV */}
        <div className="flex items-center">
          <Building2 size={14} className="text-blue-500 mr-1 flex-shrink-0" />
          <span
            className="font-medium text-sm truncate max-w-[110px]"
            title={row.original.name || "N/A"}
          >
            {row.original.name || "N/A"}
          </span>

          {row.original.isEv && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-green-200 text-green-900 rounded-full font-semibold border border-green-300">
              EV
            </span>
          )}
        </div>

        {/* Row 2: Brand / Partner */}
        <span className="text-[12px] text-gray-500 mt-[1px]">
          {row.original.isBrand ? "Brand" : "Partner"}
        </span>
      </div>
    ),
    size: 120
  },

  {
    id: 'groupId',
    header: 'Group ID',
    accessorKey: 'groupId',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-sm truncate max-w-[90px] block" title={row.original.groupId || "N/A"}>
          {row.original.groupId || "N/A"}
        </span>
        {row.original.lender && (
          <span className={`mt-1 px-2 py-0.5 text-xs rounded-full font-semibold inline-flex items-center w-min whitespace-nowrap ${getLenderBadgeColor(row.original.lender)}`}>
            {row.original.lender}
          </span>
        )}
      </div>
    ),
    size: 100
  },
  {
    id: 'contactNo',
    header: 'Contact',
    accessorKey: 'mobileNo',
    cell: ({ row }) => (
      <div className="flex items-center">
        <Phone size={12} className="text-gray-500 mr-1 flex-shrink-0" />
        <span className="text-sm truncate max-w-[80px]" title={row.original.mobileNo || "N/A"}>
          {row.original.mobileNo || "N/A"}
        </span>
      </div>
    ),
    size: 90
  },
  {
    id: 'contactEmail',
    header: 'Email',
    accessorKey: 'email',
    cell: ({ row }) => (
      <div className="flex items-center">
        <Mail size={12} className="text-gray-500 mr-1 flex-shrink-0" />
        <span className="text-sm truncate max-w-[120px]" title={row.original.email || "N/A"}>
          {row.original.email || "N/A"}
        </span>
      </div>
    ),
    size: 130
  },
  {
    id: 'createdAt',
    header: 'Created At',
    accessorKey: 'createdAt',
    cell: ({ row }) => (
      <div className="flex items-center">
        <Calendar size={12} className="text-gray-500 mr-1 flex-shrink-0" />
        <span className="text-sm" title={row.original.createdAt ? formatDate(row.original.createdAt) : "N/A"}>
          {row.original.createdAt ? formatDate(row.original.createdAt) : "N/A"}
        </span>
      </div>
    ),
    size: 100
  }
];

// Handle page changes
export const handlePageChange = (setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setPage: React.Dispatch<React.SetStateAction<number>>,
  newPage: number) => {
  // Reset data first to avoid flicker with wrong data
  setData([]);
  setIsLoading(true);

  // Update page in next tick to ensure UI updates properly
  setTimeout(() => {
    setPage(newPage);
  }, 0);
};

// Handle page size changes
export const handlePageSizeChange = (setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setPageSize: React.Dispatch<React.SetStateAction<number>>,
  setPage: React.Dispatch<React.SetStateAction<number>>,
  newSize: number) => {
  // Reset data and page when changing page size
  setData([]);
  setIsLoading(true);
  setPageSize(newSize);
  setPage(1); // Reset to first page
};

// Handle search changes
export const handleSearchChange = (setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  setPage: React.Dispatch<React.SetStateAction<number>>,
  searchQuery: string,
  query: string) => {
  // Prevent executing if the query is the same as current to avoid infinite loops
  if (query === searchQuery) return;

  // Reset data and page when searching
  setData([]);
  setIsLoading(true);
  setSearchQuery(query);
  setPage(1); // Reset to first page
};

// Handle sort changes
export const handleSortChange = (setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setSortBy: React.Dispatch<React.SetStateAction<string>>,
  setSortOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>,
  sortBy: string,
  sortOrder: 'asc' | 'desc',
  columnId: string,
  order: 'asc' | 'desc') => {
  // Prevent executing if the sort parameters are the same to avoid infinite loops
  if (columnId === sortBy && order === sortOrder) return;

  // Reset data when changing sort
  setData([]);
  setIsLoading(true);
  setSortBy(columnId);
  setSortOrder(order);
}; 